import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mail, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import BackToHome from '../components/BackToHome';

const ForgotPassword = () => {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/forgot-password', { email });
      setSent(true);
      if (response.data.email_sent) {
        toast.success(language === 'ar' ? 'تم إرسال رابط استعادة كلمة المرور إلى بريدك الإلكتروني' : 'Password reset link sent to your email');
      } else {
        toast.info(language === 'ar' ? 'إذا كان البريد مسجلاً، سيتم إرسال رابط الاستعادة' : 'If email is registered, a reset link will be sent');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'حدث خطأ' : 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const Arrow = language === 'ar' ? ArrowLeft : ArrowRight;

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <BackToHome />
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary mb-2">KE Gents Saloon</h1>
          <p className="text-muted-foreground">
            {language === 'ar' ? 'استعادة كلمة المرور' : 'Password Recovery'}
          </p>
        </div>

        <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-8">
          {!sent ? (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-xl font-semibold mb-2">
                  {language === 'ar' ? 'نسيت كلمة المرور؟' : 'Forgot Password?'}
                </h2>
                <p className="text-muted-foreground text-sm">
                  {language === 'ar' 
                    ? 'أدخل بريدك الإلكتروني وسنرسل لك رابط لاستعادة كلمة المرور' 
                    : 'Enter your email and we\'ll send you a reset link'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@email.com"
                    required
                    className="bg-secondary/50 border-white/10"
                    dir="ltr"
                    data-testid="forgot-password-email-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-primary text-primary-foreground"
                  disabled={loading}
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? (
                    language === 'ar' ? 'جاري الإرسال...' : 'Sending...'
                  ) : (
                    <>
                      {language === 'ar' ? 'إرسال رابط الاستعادة' : 'Send Reset Link'}
                      <Arrow className="w-4 h-4 ms-2" />
                    </>
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold mb-2">
                {language === 'ar' ? 'تم الإرسال!' : 'Email Sent!'}
              </h2>
              <p className="text-muted-foreground text-sm mb-4">
                {language === 'ar' 
                  ? 'إذا كان البريد الإلكتروني مسجلاً، ستتلقى رابط استعادة كلمة المرور قريباً.' 
                  : 'If the email is registered, you will receive a password reset link shortly.'}
              </p>
              <p className="text-muted-foreground text-xs">
                {language === 'ar' 
                  ? 'تحقق من صندوق الوارد والبريد غير المرغوب فيه' 
                  : 'Check your inbox and spam folder'}
              </p>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <Link 
              to="/login" 
              className="text-primary hover:underline text-sm inline-flex items-center gap-2"
            >
              {language === 'ar' ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
              {language === 'ar' ? 'العودة لتسجيل الدخول' : 'Back to Login'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
